"use client";

import { Check, ChevronDown } from "lucide-react";
import { memo, useCallback, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type TagFilterState = "neutral" | "include";

export interface TagFilterValue {
	[tag: string]: TagFilterState;
}

interface TagFilterSelectProps {
	tags: string[];
	value: TagFilterValue;
	onChange: (value: TagFilterValue) => void;
	placeholder?: string;
}

export const TagFilterSelect = memo(function TagFilterSelect({
	tags,
	value,
	onChange,
	placeholder = "Tags",
}: TagFilterSelectProps) {
	const activeCount = useMemo(
		() => Object.values(value).filter((s) => s === "include").length,
		[value],
	);

	const handleToggle = useCallback(
		(tag: string) => {
			const current = value[tag] || "neutral";
			const next: TagFilterState =
				current === "include" ? "neutral" : "include";
			const updated = { ...value, [tag]: next };
			if (next === "neutral") delete updated[tag];
			onChange(updated);
		},
		[value, onChange],
	);

	if (tags.length === 0) return null;

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className={cn(
						"w-28 h-9 text-xs gap-1.5 justify-between",
						activeCount > 0 && "border-primary text-primary",
					)}
				>
					<div className="flex items-center gap-1.5">
						<span>{placeholder}</span>
						{activeCount > 0 && (
							<Badge
								variant="secondary"
								className="h-4 px-1 min-w-4 text-[10px] font-bold"
							>
								{activeCount}
							</Badge>
						)}
					</div>
					<ChevronDown className="h-3.5 w-3.5 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[calc(100vw-2rem)] sm:w-56 p-2 max-h-72 overflow-y-auto"
				align="start"
			>
				<div className="space-y-1">
					{tags.map((tag) => {
						const state = value[tag] || "neutral";
						return (
							<button
								key={tag}
								type="button"
								onClick={() => handleToggle(tag)}
								className={cn(
									"flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer text-left",
									state === "include"
										? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
										: "hover:bg-muted text-foreground",
								)}
							>
								<span
									className={cn(
										"flex items-center justify-center w-4 h-4 rounded border shrink-0",
										state === "include"
											? "bg-emerald-500 border-emerald-500 text-white"
											: "border-muted-foreground/30",
									)}
								>
									{state === "include" && <Check className="w-3 h-3" />}
								</span>
								<span className="truncate">{tag}</span>
							</button>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
});

export function filterProductsByTags<T extends { tags?: string[] }>(
	products: T[],
	tagFilter: TagFilterValue,
): T[] {
	const includeTags = Object.entries(tagFilter)
		.filter(([, state]) => state === "include")
		.map(([tag]) => tag.toLowerCase());

	if (includeTags.length === 0) return products;

	return products.filter((product) => {
		const productTags = (product.tags || []).map((t) => t.toLowerCase());
		return includeTags.some((tag) => productTags.includes(tag));
	});
}
